/**
 * 起動時ブートストラップ (§6): シード注入とローカルデータ移行。
 *
 * シード注入 (§6-5): 「コレクションが空なら注入」はレース (一斉初回ログインの同時
 * ゼロ観測) と永久復活ループ (全削除→誰かの起動で復活) を生むため不採用。
 * meta/seeds のフラグ doc を runTransaction で「未設定なら設定+シード書込」する。
 * バージョン bump による上書き再注入はしない — 再投入は管理者の明示操作のみ。
 *
 * インポート (§6-1/§6-4): migrations/{email} 未完了かつ localStorage にユーザー作成
 * 物件 (シード/サンプル id 以外) があれば提案。固定 id は既存優先で無条件 skip。
 */
import { getFirebase } from '@/lib/firebase/init';
import { serializeDoc } from '@/lib/firebase/serializer';
import seedData from '@/data/properties.seed.json';
import evacuationSeeds from '@/data/evacuation-seeds.json';
import smokingSeeds from '@/data/smoking-seeds.json';
import knowledgeSeeds from '@/data/knowledge-seeds.json';
import type { PropertyV2 } from '@/types/property-v2';
import type { Property } from '@/types/property';
import type { SmokingProperty } from '@/types/smoking';
import type { KnowledgeRecord } from '@/types/knowledge';
import type { BuildingMasterEntry } from '@/types/building';

const SEEDS_V2 = seedData as unknown as PropertyV2[];
export const SEED_IDS_V2: ReadonlySet<string> = new Set(SEEDS_V2.map((s) => s.id));

const SEEDS_V1 = evacuationSeeds as unknown as Property[];
export const SEED_IDS_V1: ReadonlySet<string> = new Set(SEEDS_V1.map((s) => s.id));

const SEEDS_SMOKING = smokingSeeds as unknown as SmokingProperty[];
export const SEED_IDS_SMOKING: ReadonlySet<string> = new Set(
  SEEDS_SMOKING.map((s) => s.id),
);

const SEEDS_KNOWLEDGE = knowledgeSeeds as unknown as KnowledgeRecord[];
export const SEED_IDS_KNOWLEDGE: ReadonlySet<string> = new Set(
  SEEDS_KNOWLEDGE.map((s) => s.id),
);

const V2_LOCAL_KEY = 'evacuation-map.properties.v2';

export async function ensureSeedsV2(
  email: string,
  displayName: string | null,
): Promise<'injected' | 'already'> {
  const { db } = await getFirebase();
  const { doc, runTransaction, serverTimestamp, Timestamp } = await import(
    'firebase/firestore'
  );
  return runTransaction(db, async (tx) => {
    const flagRef = doc(db, 'meta', 'seeds');
    const flag = await tx.get(flagRef);
    const current = (flag.exists() ? flag.data() : {}) as Record<string, unknown>;
    if (current.v2) return 'already' as const;
    tx.set(
      flagRef,
      { v2: { injectedAt: serverTimestamp(), by: email, count: SEEDS_V2.length } },
      { merge: true },
    );
    for (const seed of SEEDS_V2) {
      tx.set(doc(db, 'properties_v2', seed.id), {
        ...serializeDoc('properties_v2', seed as unknown as Record<string, unknown>),
        createdBy: email,
        createdByName: displayName,
        updatedBy: email,
        // firestore.rules の auditOk() が create/update で updatedBy/updatedByName の
        // トークン一致を要求する (§7)。null は「name クレーム欠落」と同義に扱われる。
        updatedByName: displayName,
        // シード JSON の元 updatedAt/createdAt を保持する (§6-1 と同じ理由: 常に
        // 注入時刻 serverTimestamp() で上書きすると、一覧の「更新日時」ソートが
        // 意味を失う)。
        createdAt: Timestamp.fromDate(new Date(seed.createdAt)),
        updatedAt: Timestamp.fromDate(new Date(seed.updatedAt)),
      });
    }
    return 'injected' as const;
  });
}

/**
 * 避難マップ (v1) の同梱シードを注入する。ensureSeedsV2 と同型で、フラグは
 * meta/seeds の `v1` フィールド (v2 と同じ doc・別フィールド)。
 */
export async function ensureSeedsV1(
  email: string,
  displayName: string | null,
): Promise<'injected' | 'already'> {
  const { db } = await getFirebase();
  const { doc, runTransaction, serverTimestamp, Timestamp } = await import(
    'firebase/firestore'
  );
  return runTransaction(db, async (tx) => {
    const flagRef = doc(db, 'meta', 'seeds');
    const flag = await tx.get(flagRef);
    const current = (flag.exists() ? flag.data() : {}) as Record<string, unknown>;
    if (current.v1) return 'already' as const;
    tx.set(
      flagRef,
      { v1: { injectedAt: serverTimestamp(), by: email, count: SEEDS_V1.length } },
      { merge: true },
    );
    for (const seed of SEEDS_V1) {
      tx.set(doc(db, 'properties_v1', seed.id), {
        ...serializeDoc('properties_v1', seed as unknown as Record<string, unknown>),
        createdBy: email,
        createdByName: displayName,
        updatedBy: email,
        // firestore.rules の auditOk() が create/update で updatedBy/updatedByName の
        // トークン一致を要求する (§7)。null は「name クレーム欠落」と同義に扱われる。
        updatedByName: displayName,
        // シード JSON の元 createdAt/updatedAt を保持する (§6-1: 常に注入時刻で
        // 上書きすると一覧の「更新日時」ソートが意味を失う)。
        createdAt: Timestamp.fromDate(new Date(seed.createdAt)),
        updatedAt: Timestamp.fromDate(new Date(seed.updatedAt)),
      });
    }
    return 'injected' as const;
  });
}

/**
 * 喫煙所マップの同梱シードを注入する。ensureSeedsV1/V2 と同型で、フラグは
 * meta/seeds の `smoking` フィールド。
 */
export async function ensureSeedsSmoking(
  email: string,
  displayName: string | null,
): Promise<'injected' | 'already'> {
  const { db } = await getFirebase();
  const { doc, runTransaction, serverTimestamp, Timestamp } = await import(
    'firebase/firestore'
  );
  return runTransaction(db, async (tx) => {
    const flagRef = doc(db, 'meta', 'seeds');
    const flag = await tx.get(flagRef);
    const current = (flag.exists() ? flag.data() : {}) as Record<string, unknown>;
    if (current.smoking) return 'already' as const;
    tx.set(
      flagRef,
      {
        smoking: {
          injectedAt: serverTimestamp(),
          by: email,
          count: SEEDS_SMOKING.length,
        },
      },
      { merge: true },
    );
    for (const seed of SEEDS_SMOKING) {
      tx.set(doc(db, 'smoking_properties', seed.id), {
        ...serializeDoc(
          'smoking_properties',
          seed as unknown as Record<string, unknown>,
        ),
        createdBy: email,
        createdByName: displayName,
        updatedBy: email,
        // firestore.rules の auditOk() が create/update で updatedBy/updatedByName の
        // トークン一致を要求する (§7)。null は「name クレーム欠落」と同義に扱われる。
        updatedByName: displayName,
        // シード JSON の元 createdAt/updatedAt を保持する (§6-1)。
        createdAt: Timestamp.fromDate(new Date(seed.createdAt)),
        updatedAt: Timestamp.fromDate(new Date(seed.updatedAt)),
      });
    }
    return 'injected' as const;
  });
}

/**
 * 避難先ナレッジの初期シード (docs/evacuation_sites 由来の136件) を注入する。
 * ensureSeedsV1/V2/Smoking と同型で、フラグは meta/seeds の `knowledge` フィールド。
 * 以後のレコードはアプリでの出典つき保存 (knowledgeStore.recordDecision) が蓄積する。
 */
export async function ensureSeedsKnowledge(
  email: string,
  displayName: string | null,
): Promise<'injected' | 'already'> {
  const { db } = await getFirebase();
  const { doc, runTransaction, serverTimestamp, Timestamp } = await import(
    'firebase/firestore'
  );
  return runTransaction(db, async (tx) => {
    const flagRef = doc(db, 'meta', 'seeds');
    const flag = await tx.get(flagRef);
    const current = (flag.exists() ? flag.data() : {}) as Record<string, unknown>;
    if (current.knowledge) return 'already' as const;
    tx.set(
      flagRef,
      {
        knowledge: {
          injectedAt: serverTimestamp(),
          by: email,
          count: SEEDS_KNOWLEDGE.length,
        },
      },
      { merge: true },
    );
    for (const seed of SEEDS_KNOWLEDGE) {
      tx.set(doc(db, 'knowledge_records', seed.id), {
        ...serializeDoc(
          'knowledge_records',
          seed as unknown as Record<string, unknown>,
        ),
        createdBy: email,
        createdByName: displayName,
        updatedBy: email,
        // firestore.rules の auditOk() が create/update で updatedBy/updatedByName の
        // トークン一致を要求する (§7)。
        updatedByName: displayName,
        // レコードの判断日 (recordedAt) を createdAt/updatedAt に写す (§6-1 と同趣旨:
        // 注入時刻で埋めると一覧・経過表示が意味を失う)。
        createdAt: Timestamp.fromDate(new Date(seed.recordedAt)),
        updatedAt: Timestamp.fromDate(new Date(seed.recordedAt)),
      });
    }
    return 'injected' as const;
  });
}

export interface LocalImportPlan {
  userDocs: PropertyV2[];
  done: boolean;
}

export async function planLocalImportV2(email: string): Promise<LocalImportPlan> {
  const { db } = await getFirebase();
  const { doc, getDoc } = await import('firebase/firestore');
  const mig = await getDoc(doc(db, 'migrations', email));
  if (mig.exists() && (mig.data() as { v2ImportedAt?: unknown }).v2ImportedAt) {
    return { userDocs: [], done: true };
  }
  let local: PropertyV2[] = [];
  try {
    const raw = localStorage.getItem(V2_LOCAL_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) local = parsed as PropertyV2[];
    }
  } catch {
    local = [];
  }
  // シード (固定 id) は移行対象外 — 既存優先で無条件 skip (§6-1)
  const userDocs = local.filter((p) => !SEED_IDS_V2.has(p.id));
  return { userDocs, done: false };
}

/** writeBatch 1件あたりの上限 (Firestore の実制限は500)。 */
const IMPORT_BATCH_SIZE = 500;

export async function runLocalImportV2(
  email: string,
  docs: PropertyV2[],
  displayName: string | null,
): Promise<void> {
  const { db } = await getFirebase();
  const { doc, writeBatch, serverTimestamp, Timestamp } = await import(
    'firebase/firestore'
  );
  // 500件超のインポートは1バッチに収まらないため分割する。docs が空でも
  // migrations フラグ書込のため最低1バッチは実行する (Math.max(1, ...))。
  const chunkCount = Math.max(1, Math.ceil(docs.length / IMPORT_BATCH_SIZE));
  for (let i = 0; i < chunkCount; i++) {
    const chunk = docs.slice(i * IMPORT_BATCH_SIZE, (i + 1) * IMPORT_BATCH_SIZE);
    const batch = writeBatch(db);
    for (const p of chunk) {
      batch.set(doc(db, 'properties_v2', p.id), {
        ...serializeDoc('properties_v2', p as unknown as Record<string, unknown>),
        createdBy: email,
        createdByName: displayName,
        updatedBy: email,
        // firestore.rules の auditOk() が create/update で updatedBy/updatedByName の
        // トークン一致を要求する (§7)。null は「name クレーム欠落」と同義に扱われる。
        updatedByName: displayName,
        // 元 doc の updatedAt/createdAt (ISO文字列) を保持する — 常にインポート
        // 時刻で上書きすると、一覧の「更新日時」ソートが意味を失う。
        createdAt: Timestamp.fromDate(new Date(p.createdAt)),
        updatedAt: Timestamp.fromDate(new Date(p.updatedAt)),
      });
    }
    if (i === chunkCount - 1) {
      // migrations フラグは最後のバッチにまとめて含める。
      batch.set(
        doc(db, 'migrations', email),
        { v2ImportedAt: serverTimestamp(), v2Count: docs.length },
        { merge: true },
      );
    }
    await batch.commit();
  }
}

/**
 * 物件マスタ (HubSpot 由来) の一括取り込み。
 *
 * 1万件規模になるためストアの insert を回さず writeBatch で直接書く
 * (save-pipeline は1ドキュメント単位の debounce なので大量投入に向かない)。
 * doc id は HubSpot のレコードID をそのまま使う — これが紐付けの実体。
 * 既存 id は上書き、無い id は追加。削除はしない (HubSpot 側の名寄せは別作業)。
 */
export async function importBuildingMaster(
  email: string,
  entries: BuildingMasterEntry[],
  displayName: string | null,
  onProgress?: (done: number, total: number) => void,
): Promise<number> {
  const { db } = await getFirebase();
  const { doc, writeBatch, serverTimestamp } = await import('firebase/firestore');
  const chunkCount = Math.ceil(entries.length / IMPORT_BATCH_SIZE);
  for (let i = 0; i < chunkCount; i++) {
    const chunk = entries.slice(i * IMPORT_BATCH_SIZE, (i + 1) * IMPORT_BATCH_SIZE);
    const batch = writeBatch(db);
    for (const e of chunk) {
      batch.set(doc(db, 'building_master', e.id), {
        ...serializeDoc('building_master', e as unknown as Record<string, unknown>),
        createdBy: email,
        createdByName: displayName,
        updatedBy: email,
        // rules の auditOk() が create/update で updatedBy/updatedByName の一致を要求する
        updatedByName: displayName,
        updatedAt: serverTimestamp(),
      });
    }
    await batch.commit();
    onProgress?.(Math.min((i + 1) * IMPORT_BATCH_SIZE, entries.length), entries.length);
  }
  return entries.length;
}
